/**
 * Sidebar Chat Store
 *
 * Shared client-side cache for sidebar chat snapshots.
 *
 * Primary responsibilities:
 * - Persist sidebar chat data across route remounts
 * - Dedupe sidebar chat fetches and refresh bursts
 * - Patch cached snapshots after local chat updates
 *
 * Dependencies:
 * - db/chats
 * - db/chat-members
 */

import {
  getPinnedChatsByWorkspaceId,
  getRecentUnpinnedChatsByWorkspaceId
} from '@/db/chats'
import {
  getUserGroupChats,
  getUserPersonalChats,
  type ChatUnreadActivityFields
} from '@/db/chat-members'
import { Tables } from '@/supabase/types'

import {
  filterTombstonedChats,
  isDeletedChatTombstoned
} from '@/lib/sidebar/deleted-chat-tombstones'
import { safeConsole } from '@/lib/utils/safeConsole'

// v3: Space-scoped chats belong in the main list (`{emoji} / {name}`).
const SIDEBAR_CHAT_STORAGE_PREFIX = 'sidebar_chat_snapshot:v3:'
const SIDEBAR_CHAT_CACHE_TTL_MS = 30_000
const SIDEBAR_CHAT_FETCH_TIMEOUT_MS = 8_000

type ChatRow = Tables<'chats'>

export interface SidebarChatSnapshot {
  workspaceId: string
  userId: string
  pinnedChats: ChatRow[]
  recentChats: ChatRow[]
  mergedChats: ChatRow[]
  chatOffset: number
  hasMoreChats: boolean
  fetchedAt: number
  signature: string
}

interface SidebarChatSnapshotOptions {
  workspaceId: string
  userId: string
}

interface LoadSidebarChatSnapshotOptions extends SidebarChatSnapshotOptions {
  recentLimit?: number
  force?: boolean
}

type SnapshotUpdater = (
  snapshot: SidebarChatSnapshot
) => SidebarChatSnapshot | null

type ChatUpdater = (chat: ChatRow) => ChatRow | null

const sidebarChatSnapshots = new Map<string, SidebarChatSnapshot>()
const sidebarChatInflight = new Map<string, Promise<SidebarChatSnapshot>>()
const pendingSnapshotStorageWrites = new Map<string, SidebarChatSnapshot>()
let isSnapshotStorageFlushScheduled = false

// --------------

const getSnapshotCacheKey = ({ workspaceId, userId }: SidebarChatSnapshotOptions) =>
  `${workspaceId}:${userId}`

const getStorageKey = (cacheKey: string) => `${SIDEBAR_CHAT_STORAGE_PREFIX}${cacheKey}`

const getChatTimestamp = (chat: Pick<ChatRow, 'updated_at' | 'created_at'>) => {
  const timestamp = Date.parse(chat.updated_at ?? chat.created_at ?? '')
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY
}

const sortChatsByActivity = (chats: ChatRow[]) =>
  [...chats].sort(
    (left, right) => getChatTimestamp(right) - getChatTimestamp(left)
  )

/**
 * Rows that belong in the main workspace chat sidebar.
 * Space-scoped chats (`space_id` set) and Space conversation chats
 * (`type === 'space_conversation'`) are included — titles render as
 * `{space emoji} / {chat name}` via `formatSpaceChatListTitle`.
 * Sidekick rows stay out of the main list.
 */
export const isNormalSidebarChat = (chat: ChatRow) =>
  chat.type !== 'sidekick'

const withSidebarChatTimeout = async <T,>(
  promise: Promise<T>,
  label: string
): Promise<T> => {
  if (typeof window === 'undefined') {
    return promise
  }

  let timeoutId: number | null = null
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${SIDEBAR_CHAT_FETCH_TIMEOUT_MS}ms`))
    }, SIDEBAR_CHAT_FETCH_TIMEOUT_MS)
  })

  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId)
    }
  }
}

const resolveSettledChats = (
  result: PromiseSettledResult<ChatRow[]>,
  label: string
) => {
  if (result.status === 'fulfilled') {
    return result.value
  }

  safeConsole.warn(`Failed to load ${label} for sidebar chat snapshot:`, result.reason)
  return []
}

const dedupeChatGroups = (chatGroups: ChatRow[][]) => {
  const deduped = new Map<string, ChatRow>()

  chatGroups.forEach((chats) => {
    chats.forEach((chat) => {
      if (!chat?.id || !isNormalSidebarChat(chat)) {
        return
      }

      const existingChat = deduped.get(chat.id)

      if (!existingChat || getChatTimestamp(chat) >= getChatTimestamp(existingChat)) {
        deduped.set(chat.id, chat)
      }
    })
  })

  return sortChatsByActivity(Array.from(deduped.values()))
}

const dedupeChats = (chats: ChatRow[]) => dedupeChatGroups([chats])

export const chatMatchesSidebarWorkspaceShell = (
  chat: ChatRow,
  workspaceId: string
) => Boolean(
  isNormalSidebarChat(chat) &&
  (chat.is_group_chat || !chat.workspace_id || chat.workspace_id === workspaceId)
)

export const chatListMatchesSidebarWorkspaceShell = (
  chats: ChatRow[],
  workspaceId: string
) => chats.length > 0 && chats.every((chat) => chatMatchesSidebarWorkspaceShell(chat, workspaceId))

export const mergeSidebarChatLists = (...chatGroups: ChatRow[][]) =>
  dedupeChatGroups(chatGroups)

export const reconcileSidebarWorkspaceShellChats = ({
  workspaceId,
  incomingChats,
  snapshotChats
}: {
  workspaceId: string
  incomingChats: ChatRow[]
  snapshotChats: ChatRow[]
}) => {
  const incomingChatIds = new Set(incomingChats.map((chat) => chat.id))
  const preservedJoinedGroupChats = snapshotChats.filter(
    (chat) =>
      chat.is_group_chat &&
      chat.workspace_id !== workspaceId &&
      !incomingChatIds.has(chat.id)
  )

  return mergeSidebarChatLists(incomingChats, preservedJoinedGroupChats)
}

export const getJoinedSidebarGroupChatIds = (
  chats: ChatRow[],
  workspaceId: string
) =>
  Array.from(
    new Set(
      chats
        .filter(
          (chat) =>
            chat.is_group_chat &&
            Boolean(chat.id) &&
            chat.workspace_id !== workspaceId
        )
        .map((chat) => chat.id)
    )
  )

const getUnreadActivitySignature = (chat: ChatRow) => {
  const unreadChat = chat as ChatRow & ChatUnreadActivityFields

  return unreadChat.unread_activity
    ? `1:${unreadChat.unread_activity_message_id ?? ''}:${unreadChat.unread_activity_sequence_number ?? ''}`
    : '0'
}

export const buildSidebarChatListSignature = (chats: ChatRow[]) =>
  chats
    .map(
      (chat) =>
        `${chat.id}:${chat.updated_at ?? chat.created_at ?? ''}:${chat.pinned ? '1' : '0'}:${chat.name ?? ''}:${getUnreadActivitySignature(chat)}`
    )
    .join('|')

const normalizeSnapshot = (snapshot: SidebarChatSnapshot): SidebarChatSnapshot => {
  const mergedChats = dedupeChats(snapshot.mergedChats)

  return {
    ...snapshot,
    pinnedChats: dedupeChats(snapshot.pinnedChats),
    recentChats: dedupeChats(snapshot.recentChats),
    mergedChats,
    chatOffset: snapshot.chatOffset ?? snapshot.recentChats.length,
    fetchedAt: snapshot.fetchedAt ?? Date.now(),
    signature: buildSidebarChatListSignature(mergedChats)
  }
}

const flushPendingSnapshotStorageWrites = () => {
  if (typeof window === 'undefined' || pendingSnapshotStorageWrites.size === 0) {
    return
  }

  const pendingWrites = Array.from(pendingSnapshotStorageWrites.entries())
  pendingSnapshotStorageWrites.clear()

  pendingWrites.forEach(([cacheKey, snapshot]) => {
    try {
      sessionStorage.setItem(getStorageKey(cacheKey), JSON.stringify(snapshot))
    } catch (error) {
      safeConsole.warn('Failed to persist sidebar chat snapshot:', error)
    }
  })
}

const scheduleSnapshotStorageWrite = (cacheKey: string, snapshot: SidebarChatSnapshot) => {
  if (typeof window === 'undefined') {
    return
  }

  pendingSnapshotStorageWrites.set(cacheKey, snapshot)

  if (isSnapshotStorageFlushScheduled) {
    return
  }

  isSnapshotStorageFlushScheduled = true

  const flush = () => {
    isSnapshotStorageFlushScheduled = false
    flushPendingSnapshotStorageWrites()
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(flush, { timeout: 1_000 })
    return
  }

  window.setTimeout(flush, 0)
}

const persistSnapshot = (
  cacheKey: string,
  snapshot: SidebarChatSnapshot,
  options: { writeStorage?: boolean } = {}
) => {
  const { writeStorage = true } = options
  const normalizedSnapshot = normalizeSnapshot({
    ...snapshot,
    pinnedChats: filterTombstonedChats(snapshot.pinnedChats),
    recentChats: filterTombstonedChats(snapshot.recentChats),
    mergedChats: filterTombstonedChats(snapshot.mergedChats)
  })

  sidebarChatSnapshots.set(cacheKey, normalizedSnapshot)

  if (writeStorage) {
    scheduleSnapshotStorageWrite(cacheKey, normalizedSnapshot)
  }

  return normalizedSnapshot
}

const clearSnapshot = (cacheKey: string) => {
  sidebarChatSnapshots.delete(cacheKey)
  pendingSnapshotStorageWrites.delete(cacheKey)

  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(getStorageKey(cacheKey))
    } catch (error) {
      safeConsole.warn('Failed to clear sidebar chat snapshot:', error)
    }
  }
}

export const clearAllSidebarChatSnapshots = () => {
  sidebarChatInflight.clear()
  sidebarChatSnapshots.clear()
  pendingSnapshotStorageWrites.clear()

  if (typeof window === 'undefined') {
    return
  }

  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith(SIDEBAR_CHAT_STORAGE_PREFIX))
      .forEach((key) => sessionStorage.removeItem(key))
  } catch (error) {
    safeConsole.warn('Failed to clear all sidebar chat snapshots:', error)
  }
}

const readSnapshotFromStorage = (cacheKey: string) => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const rawSnapshot = sessionStorage.getItem(getStorageKey(cacheKey))

    if (!rawSnapshot) {
      return null
    }

    const parsedSnapshot = JSON.parse(rawSnapshot) as SidebarChatSnapshot

    if (
      !parsedSnapshot?.workspaceId ||
      !parsedSnapshot?.userId ||
      !Array.isArray(parsedSnapshot.mergedChats)
    ) {
      clearSnapshot(cacheKey)
      return null
    }

    return persistSnapshot(cacheKey, parsedSnapshot, {
      writeStorage: false
    })
  } catch (error) {
    safeConsole.warn('Failed to read sidebar chat snapshot:', error)
    clearSnapshot(cacheKey)
    return null
  }
}

const mapChats = (chats: ChatRow[], chatId: string, updater: ChatUpdater) => {
  return chats.flatMap((chat) => {
    if (chat.id !== chatId) {
      return [chat]
    }

    const updatedChat = updater(chat)
    return updatedChat ? [updatedChat] : []
  })
}

const refreshOwnedChats = (
  snapshot: SidebarChatSnapshot,
  pinnedChats: ChatRow[],
  recentChats: ChatRow[]
) => {
  const ownedChats = dedupeChats([...pinnedChats, ...recentChats]).filter(
    (chat) => chat.workspace_id === snapshot.workspaceId
  )

  return {
    pinnedChats: sortChatsByActivity(
      ownedChats.filter((chat) => Boolean(chat.pinned))
    ),
    recentChats: sortChatsByActivity(
      ownedChats.filter((chat) => !chat.pinned)
    )
  }
}

// --------------

export const isSidebarChatSnapshotFresh = (snapshot: SidebarChatSnapshot | null) => {
  if (!snapshot) {
    return false
  }

  return Date.now() - snapshot.fetchedAt < SIDEBAR_CHAT_CACHE_TTL_MS
}

export const getCachedSidebarChatSnapshot = (
  options: SidebarChatSnapshotOptions
) => {
  const cacheKey = getSnapshotCacheKey(options)
  const cachedSnapshot = sidebarChatSnapshots.get(cacheKey)

  if (cachedSnapshot) {
    return cachedSnapshot
  }

  return readSnapshotFromStorage(cacheKey)
}

export const loadSidebarChatSnapshot = async ({
  workspaceId,
  userId,
  recentLimit = 20,
  force = false
}: LoadSidebarChatSnapshotOptions): Promise<SidebarChatSnapshot> => {
  const cacheKey = getSnapshotCacheKey({ workspaceId, userId })
  const cachedSnapshot = getCachedSidebarChatSnapshot({ workspaceId, userId })

  if (!force && isSidebarChatSnapshotFresh(cachedSnapshot)) {
    return cachedSnapshot as SidebarChatSnapshot
  }

  const inflightSnapshot = sidebarChatInflight.get(cacheKey)
  if (inflightSnapshot) {
    return inflightSnapshot
  }

  const loadPromise = (async () => {
    const [pinnedResult, recentResult, groupResult, personalResult] = await Promise.allSettled([
      withSidebarChatTimeout(getPinnedChatsByWorkspaceId(workspaceId), 'Pinned chats'),
      withSidebarChatTimeout(
        getRecentUnpinnedChatsByWorkspaceId(workspaceId, recentLimit, 0),
        'Recent chats'
      ),
      withSidebarChatTimeout(getUserGroupChats(userId, workspaceId), 'Group chats'),
      withSidebarChatTimeout(getUserPersonalChats(userId, workspaceId), 'Personal chats')
    ])

    const pinnedChats = resolveSettledChats(pinnedResult, 'pinned chats')
    const recentChats = resolveSettledChats(recentResult, 'recent chats')
    const groupChats = resolveSettledChats(groupResult, 'group chats')
    const personalChats = resolveSettledChats(personalResult, 'personal chats')

    const mergedChats = mergeSidebarChatLists(
      pinnedChats,
      recentChats,
      groupChats,
      personalChats
    )

    return persistSnapshot(cacheKey, {
      workspaceId,
      userId,
      pinnedChats,
      recentChats,
      mergedChats,
      chatOffset: recentChats.length,
      hasMoreChats: recentChats.length === recentLimit,
      fetchedAt: Date.now(),
      signature: ''
    })
  })()

  sidebarChatInflight.set(cacheKey, loadPromise)

  try {
    return await loadPromise
  } finally {
    sidebarChatInflight.delete(cacheKey)
  }
}

export const updateSidebarChatSnapshot = (
  options: SidebarChatSnapshotOptions,
  updater: SnapshotUpdater
) => {
  const cacheKey = getSnapshotCacheKey(options)
  const currentSnapshot = getCachedSidebarChatSnapshot(options)

  if (!currentSnapshot) {
    return null
  }

  const updatedSnapshot = updater(currentSnapshot)

  if (!updatedSnapshot) {
    clearSnapshot(cacheKey)
    return null
  }

  return persistSnapshot(cacheKey, updatedSnapshot)
}

export const syncSidebarChatSnapshotChats = (
  options: SidebarChatSnapshotOptions,
  chats: ChatRow[]
) => {
  return updateSidebarChatSnapshot(options, (snapshot) => ({
    ...snapshot,
    mergedChats: reconcileSidebarWorkspaceShellChats({
      workspaceId: snapshot.workspaceId,
      incomingChats: chats,
      snapshotChats: snapshot.mergedChats
    })
  }))
}

export const patchSidebarChatSnapshots = (chatId: string, updater: ChatUpdater) => {
  sidebarChatSnapshots.forEach((snapshot, cacheKey) => {
    if (!snapshot.mergedChats.some((chat) => chat.id === chatId)) {
      return
    }

    const patchedPinnedChats = mapChats(snapshot.pinnedChats, chatId, updater)
    const patchedRecentChats = mapChats(snapshot.recentChats, chatId, updater)
    const patchedMergedChats = mapChats(snapshot.mergedChats, chatId, updater)
    const refreshedOwnedChats = refreshOwnedChats(
      snapshot,
      patchedPinnedChats,
      patchedRecentChats
    )

    persistSnapshot(cacheKey, {
      ...snapshot,
      ...refreshedOwnedChats,
      mergedChats: patchedMergedChats,
      chatOffset: refreshedOwnedChats.recentChats.length,
      fetchedAt: Date.now(),
      hasMoreChats: snapshot.hasMoreChats
    })
  })
}

export const removeSidebarChatSnapshots = (chatId: string) => {
  patchSidebarChatSnapshots(chatId, () => null)
}

export const prependSidebarChatSnapshots = (
  chat: ChatRow,
  options?: {
    userId?: string | null
  }
) => {
  const targetUserId = options?.userId ?? chat.user_id

  if (!targetUserId) {
    return
  }

  // Never re-seed a chat the user just deleted (stale updateChat / create race).
  if (isDeletedChatTombstoned(chat.id)) {
    return
  }

  sidebarChatSnapshots.forEach((snapshot, cacheKey) => {
    if (snapshot.userId !== targetUserId) {
      return
    }

    const shouldIncludeInSnapshot = chat.is_group_chat
      ? chatMatchesSidebarWorkspaceShell(chat, snapshot.workspaceId)
      : isNormalSidebarChat(chat) && snapshot.workspaceId === chat.workspace_id

    if (!shouldIncludeInSnapshot) {
      return
    }

    const shouldSeedOwnedLists = snapshot.workspaceId === chat.workspace_id
    const pinnedChats =
      shouldSeedOwnedLists && chat.pinned
        ? dedupeChats([chat, ...snapshot.pinnedChats])
        : snapshot.pinnedChats
    const recentChats =
      shouldSeedOwnedLists && !chat.pinned
        ? dedupeChats([chat, ...snapshot.recentChats])
        : snapshot.recentChats

    persistSnapshot(cacheKey, {
      ...snapshot,
      pinnedChats,
      recentChats,
      mergedChats: mergeSidebarChatLists(
        chat.pinned ? [chat] : [],
        chat.pinned ? [] : [chat],
        snapshot.mergedChats
      ),
      chatOffset: shouldSeedOwnedLists ? recentChats.length : snapshot.chatOffset,
      fetchedAt: Date.now()
    })
  })
}
